import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Box, Button, Flex, IconButton, Spinner, Text } from '@radix-ui/themes';
import { ChevronLeft, Moon, Sun } from 'lucide-react';
import { CONSOLE_PROJECT, MAX_FILE_BYTES } from '@mac-remote/protocol';
import type {
  AppList,
  AppWindow,
  CodexUsage,
  LinkState,
  MacMetrics,
  PowerMode,
  Project,
  Session,
} from '@mac-remote/protocol';
import { Connection, type ConnState } from './lib/connection';
import { getTelegramWebApp } from './lib/telegram';
import { loadHistory, loadSnippets, pushHistory, saveSnippets } from './lib/prefs';
import type { KeyName } from './lib/xtermInput';
import type { MenuItem } from './lib/menu';
import { LinkBadge } from './components/LinkBadge';
import { KeyBar } from './components/KeyBar';
import { Term, type TermHandle } from './components/Term';
import { TermText } from './components/TermText';
import { Composer } from './components/Composer';
import { Snippets } from './components/Snippets';
import { SessionTabs } from './components/SessionTabs';
import { MenuButtons } from './components/MenuButtons';
import { MacBadge } from './components/MacBadge';
import { CodexBadge } from './components/CodexBadge';
import { Projects } from './screens/Projects';
import { Console } from './screens/Console';
import { Mac } from './screens/Mac';
import { Apps } from './screens/Apps';
import { Screen, type Shot } from './screens/Screen';
import { Browser } from './screens/Browser';
import { Files } from './screens/Files';
import { System } from './screens/System';
import { FilePreview, type PreviewFile } from './screens/FilePreview';
import { fileKind } from './lib/fileKind';
import { useMediaQuery } from './lib/useMediaQuery';
import { projectLabel } from './lib/projectLabel';
import type { FsEntry, Proc } from '@mac-remote/protocol';

const FONT_KEY = 'mac-remote:font';
const b64 = (s: string) => btoa(String.fromCharCode(...new TextEncoder().encode(s)));

export function App() {
  const conn = useMemo(() => new Connection(), []);
  const [connState, setConnState] = useState<ConnState>({ kind: 'connecting' });
  const [link, setLink] = useState<LinkState>('offline');
  const [latency, setLatency] = useState<number | null>(null);
  const [lastSeen, setLastSeen] = useState<number | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  /**
   * Project sessions only. The console is a session on the wire like any other, but it
   * is not a project and has its own screen, so it must not turn up as a tab in the
   * project view or as an unnamed chip labelled `__console__`.
   */
  const projectSessions = useMemo(() => sessions.filter((s) => s.id !== CONSOLE_PROJECT), [sessions]);
  const [sid, setSid] = useState<string | null>(null);
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [power, setPower] = useState<PowerMode>('awake');
  const [draft, setDraft] = useState('');
  const [snippets, setSnippets] = useState<string[]>(() => loadSnippets('codex'));
  const [history, setHistory] = useState<string[]>(() => loadHistory('codex'));
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [metrics, setMetrics] = useState<MacMetrics | null>(null);
  const [codexUsage, setCodexUsage] = useState<CodexUsage | null>(null);
  const [showMac, setShowMac] = useState(false);
  const [macTab, setMacTab] = useState<'state' | 'apps' | 'screen' | 'browser' | 'files' | 'system' | 'console'>('state');
  const [screen, setScreen] = useState<Shot | null>(null);
  const [screenPending, setScreenPending] = useState(false);
  const [screenAuto, setScreenAuto] = useState(false);
  const [files, setFiles] = useState<{ path: string; entries: FsEntry[] } | null>(null);
  const [filesPending, setFilesPending] = useState(false);
  const [clipText, setClipText] = useState<string | null>(null);
  const [procs, setProcs] = useState<Proc[] | null>(null);
  const [webcam, setWebcam] = useState<Shot | null>(null);
  const [sysPending, setSysPending] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewFile | null>(null);
  /** Path of the file currently on its way to the Telegram chat, and the name of the last
   *  one that got there. Two fields rather than one status: the sheet has to be able to say
   *  «отправляю» and «в чате» about the same file without them being the same state. */
  const [sendingFile, setSendingFile] = useState<string | null>(null);
  const [sentFile, setSentFile] = useState<string | null>(null);
  /** A snapshot of the terminal, taken when it was asked for. `null` means the sheet
   *  is closed — an empty session still has a sheet to open, it is just empty. */
  const [termText, setTermText] = useState<string | null>(null);
  const wide = useMediaQuery('(min-width: 900px)');
  const [appList, setAppList] = useState<AppList | null>(null);
  const [appPending, setAppPending] = useState<string | null>(null);
  const [appWindows, setAppWindows] = useState<Record<string, AppWindow[] | undefined>>({});
  const [expandedApp, setExpandedApp] = useState<string | null>(null);
  /** Set while a graceful quit is in flight, so a failure can offer to escalate and
   *  name the app it is talking about. */
  const quitTarget = useRef<{ bundleId: string; name: string } | null>(null);
  const [fontSize, setFontSize] = useState(() => Number(localStorage.getItem(FONT_KEY)) || 11);
  const sidRef = useRef<string | null>(null);
  sidRef.current = sid;
  const termRef = useRef<TermHandle>(null);

  useEffect(() => {
    const offs = [
      conn.on('conn', setConnState),
      conn.on('link', (s, l, seen) => {
        setLink(s);
        setLatency(l);
        setLastSeen(seen);
      }),
      conn.on('projects', setProjects),
      conn.on('sessions', setSessions),
      conn.on('busy', (s, isBusy) => {
        setBusy((prev) => {
          const next = new Set(prev);
          if (isBusy) next.add(s);
          else next.delete(s);
          return next;
        });
        // A buzz when the work lands is the difference between watching a terminal
        // and being told it is done.
        if (!isBusy && sidRef.current === s) {
          getTelegramWebApp()?.HapticFeedback?.notificationOccurred('success');
        }
      }),
      conn.on('exit', (s) => {
        if (sidRef.current === s) setSid(null);
        // A session that ended is one the list still claims is there. Re-ask rather than
        // patching it locally — that is what tells the console screen its shell is gone
        // (`exit`, Ctrl+D) so it can offer to start a new one.
        conn.send({ t: 'c.list_sessions' });
      }),
      conn.on('power', setPower),
      conn.on('metrics', setMetrics),
      conn.on('codexUsage', setCodexUsage),
      conn.on('apps', (list) => {
        setAppList(list);
        setAppPending(null);
        quitTarget.current = null;
        // Window lists belong to a moment; an app that just quit or launched makes
        // every cached one suspect, so they are dropped rather than shown stale.
        setAppWindows({});
      }),
      conn.on('appWindows', ({ bundleId, windows }) => {
        setAppWindows((prev) => ({ ...prev, [bundleId]: windows }));
        setAppPending(null);
      }),
      conn.on('screen', (b64, w, h, at) => {
        setScreen({ b64, w, h, at });
        setScreenPending(false);
      }),
      conn.on('fs', (path, entries) => {
        setFiles({ path, entries });
        setFilesPending(false);
      }),
      conn.on('file', (name, b64) => {
        // Bytes for a sheet that is already open — it was opened by the tap, with the name
        // and size already known, and has been showing a spinner since. Never opens one by
        // itself: a file arriving after the sheet was dismissed must not reopen it.
        setPreview((prev) => (prev && prev.name === name ? { ...prev, b64 } : prev));
      }),
      conn.on('fileSent', (name) => {
        setSendingFile(null);
        setSentFile(name);
        getTelegramWebApp()?.HapticFeedback?.notificationOccurred('success');
      }),
      conn.on('clip', (text) => setClipText(text)),
      conn.on('ps', (list) => {
        setProcs(list);
        setSysPending(null);
      }),
      conn.on('webcam', (b64, w, h, at) => {
        setWebcam({ b64, w, h, at });
        setSysPending(null);
      }),
      conn.on('error', (msg) => {
        const target = quitTarget.current;
        quitTarget.current = null;
        setAppPending(null);
        setScreenPending(false);
        setFilesPending(false);
        setSysPending(null);
        setSendingFile(null);
        // A sheet still waiting for bytes is the one thing this error could be about —
        // it is the only pending file request there can be. Show it there instead of
        // leaving a spinner running under a dismissed alert.
        setPreview((prev) => (prev && prev.b64 === null ? { ...prev, error: msg } : prev));
        const wa = getTelegramWebApp();
        if (!target) {
          wa?.showAlert?.(msg);
          return;
        }
        // Any failed graceful quit is a fair place to offer escalation — including
        // the missing-Automation-permission case, because signals need no permission
        // at all and force quit will work even when the polite path cannot.
        const q = `${target.name}: ${msg}\n\nЗакрыть принудительно? Несохранённое пропадёт.`;
        const go = (yes: boolean) => {
          if (!yes) return;
          setAppPending(target.bundleId);
          conn.send({ t: 'c.app_quit', bundleId: target.bundleId, force: true });
        };
        if (wa?.showConfirm) wa.showConfirm(q, go);
        else go(window.confirm(q));
      }),
    ];
    void conn.connect();
    return () => {
      offs.forEach((off) => off());
      conn.close();
    };
  }, [conn]);

  // Fetch the project list whenever we have a working link, not just once: the very
  // first connect usually beats the Mac's own reconnect, and the list would be empty.
  useEffect(() => {
    if (connState.kind === 'ready' && link === 'online') conn.send({ t: 'c.list_projects' });
  }, [connState.kind, link, conn]);

  // Ask for the fast sampling cadence only while the numbers are actually on screen —
  // the "Состояние" tab, not the whole Mac panel: the console tab can sit open for an
  // hour and has no reason to spawn a handful of processes on a laptop every five
  // seconds. Re-sent on every (re)connect: the broker tracks this per socket, so a
  // reconnect hands us a fresh one that knows nothing about what we have open.
  useEffect(() => {
    if (connState.kind !== 'ready') return;
    conn.send({ t: 'c.metrics', watch: showMac && macTab === 'state' });
  }, [showMac, macTab, connState.kind, conn]);

  // The app list is a snapshot, not a stream — refetch whenever the tab comes up (and
  // after a reconnect), rather than keeping something days old on screen.
  useEffect(() => {
    if (connState.kind !== 'ready' || !showMac || macTab !== 'apps') return;
    conn.send({ t: 'c.apps' });
  }, [connState.kind, showMac, macTab, conn]);

  const refreshUsage = useCallback(() => {
    conn.send({ t: 'c.codex_usage' });
  }, [conn]);

  /** The badge is a shortcut to the full breakdown, and asks for a fresh reading on
   *  the way — tapping it is exactly the moment the number stops being background. */
  const openLimits = useCallback(() => {
    setShowMac(true);
    setMacTab('state');
    refreshUsage();
  }, [refreshUsage]);

  const refreshScreen = useCallback(() => {
    if (link !== 'online') return;
    setScreenPending(true);
    conn.send({ t: 'c.screenshot' });
  }, [conn, link]);

  // Grab a frame the moment the Screen tab opens, then keep grabbing on a slow timer
  // only while Auto is on. 3s, not video: a JPEG on demand is a fraction of the bytes
  // and enough to watch a GUI app. Runs only while the tab is actually up.
  useEffect(() => {
    if (connState.kind !== 'ready' || !showMac || macTab !== 'screen' || link !== 'online') return;
    refreshScreen();
    if (!screenAuto) return;
    const id = window.setInterval(refreshScreen, 3000);
    return () => window.clearInterval(id);
  }, [connState.kind, showMac, macTab, link, screenAuto, refreshScreen]);

  // Open the file browser at home the first time the Files tab comes up.
  useEffect(() => {
    if (connState.kind !== 'ready' || !showMac || macTab !== 'files' || link !== 'online') return;
    if (files) return;
    setFilesPending(true);
    conn.send({ t: 'c.fs_list', path: '~' });
  }, [connState.kind, showMac, macTab, link, files, conn]);

  const confirm = useCallback((q: string, go: () => void) => {
    const wa = getTelegramWebApp();
    if (wa?.showConfirm) wa.showConfirm(q, (yes) => yes && go());
    else if (window.confirm(q)) go();
  }, []);

  /** Telegram's dialog where there is one, the browser's otherwise. Written as an
   *  if/else rather than `showAlert?.(m) ?? alert(m)`, which fires both: showAlert
   *  returns undefined on success, and `??` then falls through to the fallback. */
  const alert = useCallback((message: string) => {
    const wa = getTelegramWebApp();
    if (wa?.showAlert) wa.showAlert(message);
    else window.alert(message);
  }, []);

  const fsNavigate = useCallback((path: string) => {
    setFilesPending(true);
    conn.send({ t: 'c.fs_list', path });
  }, [conn]);
  /**
   * Open a file. What that means depends on what it is.
   *
   * Images and text are drawn here, so their bytes are worth fetching. Everything else —
   * PDF above all — cannot be drawn in a Telegram WebView at all, so the bytes are never
   * pulled to the phone: the sheet opens knowing only the name and offers the chat, and
   * the file goes Mac → broker → Telegram without the phone ever holding it.
   */
  const fsOpen = useCallback(
    (path: string, name: string, size: number | null) => {
      setSentFile(null);
      const tooBig = size !== null && size > MAX_FILE_BYTES;
      setPreview({
        name,
        path,
        size,
        b64: null,
        error: tooBig
          ? `Больше потолка в ${Math.round(MAX_FILE_BYTES / 1024 / 1024)} МБ — по этому каналу такой файл за раз не пройдёт. Забери его из консоли (scp, облако) или разбей на части.`
          : null,
      });
      // Bytes only for what can be drawn, and only when they can arrive at all.
      if (!tooBig && fileKind(name, size) !== 'other') conn.send({ t: 'c.fs_get', path });
    },
    [conn],
  );

  const sendFileToChat = useCallback(
    (path: string) => {
      setSendingFile(path);
      conn.send({ t: 'c.file_send', path });
      getTelegramWebApp()?.HapticFeedback?.impactOccurred('medium');
    },
    [conn],
  );

  /**
   * Only ever reachable off mobile — the button is hidden in the iOS/Android WebView,
   * where navigating to a blob URL blanks the whole app. Telegram Desktop, Web and a
   * plain browser handle the anchor fine.
   */
  const canDownload = useMemo(() => {
    const p = getTelegramWebApp()?.platform ?? '';
    return p !== 'ios' && p !== 'android';
  }, []);

  const downloadPreview = useCallback(() => {
    if (!preview?.b64) return;
    const bytes = Uint8Array.from(atob(preview.b64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes]));
    const a = document.createElement('a');
    a.href = url;
    a.download = preview.name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [preview]);
  const fsUpload = useCallback((name: string, b64: string) => {
    if (!files) return;
    const sep = files.path.endsWith('/') ? '' : '/';
    conn.send({ t: 'c.fs_put', path: `${files.path}${sep}${name}`, b64 });
  }, [conn, files]);

  const screenClick = useCallback((fx: number, fy: number, right: boolean) => conn.send({ t: 'c.click', x: fx, y: fy, right }), [conn]);
  const screenType = useCallback((text: string) => conn.send({ t: 'c.type', text }), [conn]);
  const screenKeys = useCallback((combo: string) => conn.send({ t: 'c.keys', combo }), [conn]);

  const sysVolume = useCallback((level: number) => conn.send({ t: 'c.volume', level }), [conn]);
  const sysLock = useCallback(() => confirm('Заблокировать экран мака?', () => conn.send({ t: 'c.lock' })), [conn, confirm]);
  const sysRestart = useCallback(() => confirm('Перезагрузить мак? Все несохранённые данные могут пропасть.', () => conn.send({ t: 'c.restart' })), [conn, confirm]);
  const sysShutdown = useCallback(() => confirm('Выключить мак? Разбудить его удалённо будет нельзя.', () => conn.send({ t: 'c.shutdown' })), [conn, confirm]);
  const sysClipGet = useCallback(() => conn.send({ t: 'c.clip_get' }), [conn]);
  const sysClipSet = useCallback((text: string) => conn.send({ t: 'c.clip_set', text }), [conn]);
  const sysPs = useCallback(() => { setSysPending('ps'); conn.send({ t: 'c.ps' }); }, [conn]);
  const sysKill = useCallback((pid: number, name: string) => confirm(`Завершить процесс «${name}» (pid ${pid})?`, () => { conn.send({ t: 'c.kill_pid', pid }); setTimeout(() => conn.send({ t: 'c.ps' }), 500); }), [conn, confirm]);
  const sysWebcam = useCallback(() => { setSysPending('webcam'); conn.send({ t: 'c.webcam' }); }, [conn]);

  useEffect(() => localStorage.setItem(FONT_KEY, String(fontSize)), [fontSize]);
  useEffect(() => saveSnippets('codex', snippets), [snippets]);
  // A menu belongs to the session that drew it. So does a snapshot of its output.
  useEffect(() => {
    setMenu([]);
    setTermText(null);
  }, [sid]);

  /**
   * Where the text sheet reads from.
   *
   * There are two terminals now — the project session and the console — and only the one
   * currently mounted can say what is on its screen, so whichever opened the sheet also
   * leaves behind the way to re-read it. Without this the refresh button would ask the
   * session's terminal, which is unmounted while the Mac panel is up, and answer ''.
   */
  const termTextSrc = useRef<(() => string) | null>(null);

  /** Stable, so handing it to Term as a prop does not tear the terminal down and
   *  reattach the session on every render. */
  const showTermText = useCallback(() => {
    const get = () => termRef.current?.text() ?? '';
    termTextSrc.current = get;
    setTermText(get());
  }, []);

  /** Same, for the console — it owns its own terminal and hands over the reader. */
  const showConsoleText = useCallback((get: () => string) => {
    termTextSrc.current = get;
    setTermText(get());
  }, []);

  const refreshTermText = useCallback(() => {
    setTermText(termTextSrc.current?.() ?? '');
  }, []);

  // Telegram's own back button, so the app behaves like every other Mini App.
  useEffect(() => {
    const wa = getTelegramWebApp();
    if (!wa?.BackButton) return;
    // The Mac panel sits on top of whatever was open, so it unwinds first —
    // otherwise dismissing it would also drop you out of the session behind it.
    const back = () => {
      if (preview) return setPreview(null);
      if (termText !== null) return setTermText(null);
      return showMac ? setShowMac(false) : setSid(null);
    };
    if (sid || showMac || preview || termText !== null) {
      wa.BackButton.show();
      wa.BackButton.onClick(back);
    } else {
      wa.BackButton.hide();
    }
    return () => wa.BackButton.offClick(back);
  }, [sid, showMac, preview, termText]);

  const open = useCallback(
    (project: string) => {
      const existing = sessions.some((s) => s.id === project);
      // Launch Codex with the session rather than dropping into a bare shell and
      // making him tap a second button and wait. The agent types it once the shell
      // exists, so there is no race. It is still a full terminal — Ctrl-C gets out.
      if (!existing) conn.send({ t: 'c.create', project, cols: 80, rows: 24 });
      setSid(project);
      getTelegramWebApp()?.HapticFeedback?.selectionChanged();
    },
    [conn, sessions],
  );

  const send = useCallback(
    (text: string) => {
      termRef.current?.sendText(text);
      setHistory(pushHistory('codex', text));
      setMenu([]);
    },
    [],
  );

  // Routed through Term because the right bytes depend on the terminal's live modes,
  // which only the Terminal instance knows.
  const sendKey = useCallback((key: KeyName) => {
    termRef.current?.sendKey(key);
    getTelegramWebApp()?.HapticFeedback?.selectionChanged();
  }, []);

  const close = useCallback(
    (target: string) => {
      const wa = getTelegramWebApp();
      const q = `Закрыть сессию ${target}? Всё, что в ней выполняется, оборвётся.`;
      const go = (yes: boolean) => {
        if (!yes) return;
        conn.send({ t: 'c.kill', sid: target });
        if (sidRef.current === target) setSid(null);
        // Ask the Mac what is actually left rather than assuming the kill worked.
        // The agent verifies the session is gone before reporting success, so if it
        // somehow survived it reappears in this list instead of vanishing from the
        // app while still running on the Mac.
        conn.send({ t: 'c.list_sessions' });
      };
      if (wa?.showConfirm) wa.showConfirm(q, go);
      else go(window.confirm(q));
    },
    [conn],
  );

  const openApp = useCallback(
    (path: string) => {
      setAppPending(path);
      conn.send({ t: 'c.app_open', path });
      getTelegramWebApp()?.HapticFeedback?.impactOccurred('medium');
    },
    [conn],
  );

  const expandApp = useCallback(
    (bundleId: string | null) => {
      setExpandedApp(bundleId);
      // Fetch once per expansion rather than caching indefinitely: windows open and
      // close constantly, and a stale title would close the wrong one.
      if (bundleId) {
        setAppWindows((prev) => ({ ...prev, [bundleId]: undefined }));
        conn.send({ t: 'c.app_windows', bundleId });
      }
    },
    [conn],
  );

  const closeWindow = useCallback(
    (bundleId: string, index: number, title: string) => {
      setAppPending(`${bundleId}#${index}`);
      conn.send({ t: 'c.window_close', bundleId, index, title });
    },
    [conn],
  );

  const quitApp = useCallback(
    (bundleId: string, name: string) => {
      // Recorded before sending: if this comes back as an error, the handler needs to
      // know which app it was in order to offer the forced close.
      quitTarget.current = { bundleId, name };
      setAppPending(bundleId);
      conn.send({ t: 'c.app_quit', bundleId, force: false });
    },
    [conn],
  );

  const requestPower = useCallback(
    (mode: PowerMode) => {
      const wa = getTelegramWebApp();
      if (mode === 'doze') {
        // Sleeping drops the link and is the one action that can leave the Mac
        // unreachable for a few minutes — worth a confirm, like closing a session.
        const q =
          'Уложить мак спать? Он отключится и будет сам просыпаться раз в 5 мин, чтобы проверить, не нужен ли снова. Сессии сохранятся.';
        const go = (yes: boolean) => {
          if (yes) conn.send({ t: 'c.power', mode: 'doze' });
        };
        if (wa?.showConfirm) wa.showConfirm(q, go);
        else go(window.confirm(q));
      } else {
        // Waking is safe and instant to ask for — but the Mac only hears it on its
        // next scheduled poll, so it can take up to ~5 min to actually come up.
        conn.send({ t: 'c.power', mode: 'awake' });
        wa?.HapticFeedback?.impactOccurred('medium');
      }
    },
    [conn],
  );

  if (connState.kind === 'failed') {
    return (
      <Flex direction="column" align="center" justify="center" gap="3" style={{ height: '100dvh', padding: 24 }}>
        <Text size="3" weight="bold">
          {connState.reason}
        </Text>
        <Text size="2" align="center" style={{ color: 'var(--app-hint)' }}>
          {connState.reopen
            ? 'Telegram выдаёт данные для входа только при открытии приложения. Закрой и открой заново.'
            : 'Не удалось подключиться.'}
        </Text>
        <Button onClick={() => getTelegramWebApp()?.close?.()}>Закрыть</Button>
      </Flex>
    );
  }

  return (
    <div className={wide ? 'app-shell app-shell--wide' : 'app-shell'}>
    <Flex direction="column" style={{ height: '100%', minHeight: 0 }}>
      {/*
        Two zones with opposite flex rules, and that is the whole fix for the header
        that fell apart while a session was running: the status side is `0 0 auto` and
        never gives up a pixel, the title side is the only thing that shrinks. It also
        stops competing for room — the font controls moved down to the session bar and
        the wordier labels only appear when there is a desktop's width to hold them.
      */}
      <Flex align="center" justify="between" px="3" py="2" gap="2" style={{ borderBottom: '1px solid var(--gray-a4)' }}>
        <Flex align="center" gap="2" style={{ minWidth: 0, flex: '1 1 auto' }}>
          {(sid || showMac) && (
            <IconButton
              size="1"
              variant="ghost"
              color="gray"
              style={{ flex: '0 0 auto' }}
              onClick={() => (showMac ? setShowMac(false) : setSid(null))}
            >
              <ChevronLeft size={18} />
            </IconButton>
          )}
          <Text
            size="2"
            weight="bold"
            style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {showMac ? 'Мак' : sid ? projectLabel(sid, projects) : 'Проекты'}
          </Text>
          {!showMac && sid && busy.has(sid) && (
            <Flex align="center" gap="1" style={{ flex: '0 0 auto' }}>
              <Spinner size="1" />
              {wide && (
                <Text size="1" style={{ color: 'var(--jade-11)' }}>
                  работает
                </Text>
              )}
            </Flex>
          )}
        </Flex>

        <Flex align="center" gap="1" style={{ flex: '0 0 auto' }}>
          <CodexBadge usage={codexUsage} onClick={openLimits} />
          <MacBadge metrics={metrics} onClick={() => setShowMac((v) => !v)} />
          <Button
            size="1"
            variant="soft"
            color={power === 'awake' ? 'gray' : 'amber'}
            // While the Mac is on its way up after a wake request, this is a progress
            // label, not a button — a second tap here would only put it back to sleep.
            disabled={power === 'awake' && link !== 'online'}
            title={power === 'doze' ? 'Разбудить мак' : 'Усыпить мак'}
            onClick={() => requestPower(power === 'awake' ? 'doze' : 'awake')}
          >
            {power === 'awake' ? <Moon size={13} /> : <Sun size={13} />}
            {wide && (power === 'doze' ? 'Разбудить' : link === 'online' ? 'Спать' : 'Будим…')}
          </Button>
          {connState.kind === 'ready' ? (
            <LinkBadge state={link} latencyMs={latency} lastSeenAt={lastSeen} compact={!wide} />
          ) : (
            <Flex align="center" gap="2" style={{ flex: '0 0 auto' }}>
              <Spinner size="1" />
              {wide && (
                <Text size="1" style={{ color: 'var(--app-hint)', whiteSpace: 'nowrap' }}>
                  {connState.kind === 'connecting' ? 'вход…' : `переподключение (${connState.attempt})`}
                </Text>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>

      {showMac ? (
        <>
          <div className="keybar" style={{ borderBottom: '1px solid var(--gray-a4)' }}>
            <Button
              size="1"
              variant={macTab === 'state' ? 'solid' : 'soft'}
              color="gray"
              onClick={() => setMacTab('state')}
            >
              Состояние
            </Button>
            <Button
              size="1"
              variant={macTab === 'apps' ? 'solid' : 'soft'}
              color="gray"
              onClick={() => setMacTab('apps')}
            >
              Приложения
            </Button>
            <Button
              size="1"
              variant={macTab === 'screen' ? 'solid' : 'soft'}
              color="gray"
              onClick={() => setMacTab('screen')}
            >
              Экран
            </Button>
            <Button
              size="1"
              variant={macTab === 'browser' ? 'solid' : 'soft'}
              color="gray"
              onClick={() => setMacTab('browser')}
            >
              Браузер
            </Button>
            <Button
              size="1"
              variant={macTab === 'files' ? 'solid' : 'soft'}
              color="gray"
              onClick={() => setMacTab('files')}
            >
              Файлы
            </Button>
            <Button
              size="1"
              variant={macTab === 'system' ? 'solid' : 'soft'}
              color="gray"
              onClick={() => setMacTab('system')}
            >
              Система
            </Button>
            {/* Last, and marked while it is alive: a shell left running is a thing worth
                knowing about from the tab bar, since the session outlives the app. */}
            <Button
              size="1"
              variant={macTab === 'console' ? 'solid' : 'soft'}
              color={busy.has(CONSOLE_PROJECT) ? 'jade' : 'gray'}
              onClick={() => setMacTab('console')}
            >
              Консоль
              {sessions.some((s) => s.id === CONSOLE_PROJECT) && macTab !== 'console' && (
                <Badge
                  radius="full"
                  color={busy.has(CONSOLE_PROJECT) ? 'jade' : 'gray'}
                  variant="solid"
                  style={{ width: 6, height: 6, padding: 0 }}
                />
              )}
            </Button>
          </div>
          <Box style={{ flex: 1, minHeight: 0 }}>
            {macTab === 'state' ? (
              <Mac metrics={metrics} usage={codexUsage} onRefreshUsage={refreshUsage} />
            ) : macTab === 'console' ? (
              <Console
                conn={conn}
                sessions={sessions}
                link={link}
                busy={busy.has(CONSOLE_PROJECT)}
                fontSize={fontSize}
                onFontSize={setFontSize}
                onSelectText={showConsoleText}
                onConfirm={confirm}
              />
            ) : macTab === 'screen' ? (
              <Screen
                shot={screen}
                pending={screenPending}
                auto={screenAuto}
                disabled={link !== 'online'}
                onRefresh={refreshScreen}
                onToggleAuto={() => setScreenAuto((v) => !v)}
                onClick={screenClick}
                onType={screenType}
                onKeys={screenKeys}
              />
            ) : macTab === 'browser' ? (
              <Browser conn={conn} link={link} onConfirm={confirm} />
            ) : macTab === 'files' ? (
              <Files
                data={files}
                pending={filesPending}
                disabled={link !== 'online'}
                onNavigate={fsNavigate}
                onOpen={fsOpen}
                onUpload={fsUpload}
                onTooBig={alert}
              />
            ) : macTab === 'system' ? (
              <System
                disabled={link !== 'online'}
                clipText={clipText}
                procs={procs}
                webcam={webcam}
                pending={sysPending}
                onVolume={sysVolume}
                onLock={sysLock}
                onRestart={sysRestart}
                onShutdown={sysShutdown}
                onClipGet={sysClipGet}
                onClipSet={sysClipSet}
                onPs={sysPs}
                onKill={sysKill}
                onWebcam={sysWebcam}
              />
            ) : (
              <Apps
                apps={appList}
                windows={appWindows}
                pending={appPending}
                expanded={expandedApp}
                onExpand={expandApp}
                onOpen={openApp}
                onQuit={quitApp}
                onCloseWindow={closeWindow}
              />
            )}
          </Box>
        </>
      ) : sid ? (
        <>
          <SessionTabs
            sessions={projectSessions}
            active={sid}
            busy={busy}
            label={(id) => projectLabel(id, projects)}
            onSelect={setSid}
            onClose={close}
            onAdd={() => setSid(null)}
            onCopyText={showTermText}
            fontSize={fontSize}
            onFontSize={setFontSize}
          />
          <Term ref={termRef} sid={sid} conn={conn} fontSize={fontSize} onMenu={setMenu} onSelectText={showTermText} />
          <MenuButtons
            items={menu}
            onPick={(digit) => {
              // Move from the highlighted row, then confirm; no digit shortcut assumption.
              const from = menu.findIndex((item) => item.selected);
              const to = menu.findIndex((item) => item.digit === digit);
              if (from < 0 || to < 0) return;
              for (let step = 0; step < Math.abs(to - from); step++) sendKey(to > from ? 'down' : 'up');
              sendKey('enter');
              setMenu([]);
              getTelegramWebApp()?.HapticFeedback?.impactOccurred('medium');
            }}
          />
          <KeyBar onKey={sendKey} disabled={link !== 'online'} />
          <Snippets
            items={snippets}
            history={history}
            disabled={link !== 'online'}
            draft={draft}
            onRun={send}
            onPick={setDraft}
            onChange={setSnippets}
          />
          <Composer
            sid={sid}
            link={link}
            busy={busy.has(sid)}
            value={draft}
            onValueChange={setDraft}
            onSend={send}
            onInterrupt={() => sendKey('esc')}
          />
        </>
      ) : (
        <Box style={{ flex: 1, minHeight: 0 }}>
          <Projects
            projects={projects}
            sessions={projectSessions}
            busy={busy}
            usage={codexUsage}
            onOpen={open}
            onOpenLimits={openLimits}
          />
        </Box>
      )}
    </Flex>
    {termText !== null && (
      <TermText text={termText} onClose={() => setTermText(null)} onRefresh={refreshTermText} />
    )}
    {preview && (
      <FilePreview
        file={preview}
        sending={sendingFile === preview.path}
        sent={sentFile === preview.name}
        canDownload={canDownload}
        onClose={() => setPreview(null)}
        onDownload={downloadPreview}
        onSendToChat={() => sendFileToChat(preview.path)}
      />
    )}
    </div>
  );
}
